import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Advisor, CurriculumModule, Topic, TopicStatus, AdvisorProgress, CurriculumLevel, EvaluationResult, RolePlaySession, ChatMessage, EvaluationQuestion, LibraryCategory, LibraryResource, TrainingLesson } from '../types';
import { api } from '../services/api';
import Header from '../components/Header';
import TopicCard from '../components/ModuleCard';
import { SlideViewer } from './LessonBuilder';
import RolePlayView from './RolePlayPage';
import { Spinner, DownloadIcon, PlayIcon, RobotIcon, ArrowLeftIcon, BookOpenIcon, CheckCircleIcon, PencilIcon, UserGroupIcon, FolderIcon } from '../components/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const LessonTopicViewer: React.FC<{ lessonId: string }> = ({ lessonId }) => {
  const [lesson, setLesson] = useState<TrainingLesson | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getTrainingLessonPlayer(lessonId)
      .then(data => { if (!cancelled) setLesson(data); })
      .catch(() => { if (!cancelled) setLesson(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lessonId]);

  if (loading) return <div className="flex h-full items-center justify-center"><Spinner className="h-8 w-8 text-white" /></div>;
  if (!lesson) return <div className="text-center p-8 bg-slate-900 rounded-lg text-white h-full flex items-center justify-center">No se pudo cargar la lección interactiva.</div>;
  return <div className="h-full overflow-y-auto"><SlideViewer slides={lesson.slides || []} interactions={lesson.interactions || []} currentIndex={currentIndex} onSlideChange={setCurrentIndex} preview /></div>;
};

interface AdvisorDashboardProps {
  user: Advisor;
  onLogout: () => void;
  onStartEvaluation: (module: CurriculumModule) => void;
  onStartFinalEvaluation: () => void;
  logoUrl?: string;
  onRefreshUser: () => void;
}

const HeroSection: React.FC<{ user: Advisor }> = ({ user }) => {
    return (
        <div className="relative h-64 md:h-80 w-full flex items-end p-6 md:p-10 text-white bg-slate-800">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-purple-900 opacity-60"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
            <div className="relative z-10">
                <h1 className="text-3xl md:text-4xl font-bold">Bienvenido, {user.name.split(' ')[0]}</h1>
                <p className="mt-2 md:mt-4 max-w-xl text-lg text-gray-300">Tu camino para convertirte en un especialista continúa. ¿Qué te gustaría aprender hoy?</p>
            </div>
        </div>
    );
};

const EvaluationCard: React.FC<{ module: CurriculumModule, onStartEvaluation: (module: CurriculumModule) => void, evalResult?: EvaluationResult }> = ({ module, onStartEvaluation, evalResult }) => {
    return (
        <div onClick={() => onStartEvaluation(module)} className="flex-shrink-0 w-64 md:w-72 aspect-video rounded-lg overflow-hidden relative shadow-lg group transition-all duration-300 transform-gpu cursor-pointer hover:scale-105 hover:shadow-2xl hover:z-10 bg-green-800 border-2 border-green-600 flex flex-col items-center justify-center text-center p-4">
            <h3 className="text-xl font-bold text-white">{evalResult ? 'Revisar Evaluación' : 'Realizar Evaluación'}</h3>
            <p className="text-sm text-green-200 mt-2">Pon a prueba tus conocimientos del módulo "{module.title}".</p>
             <div className="mt-4 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white">
                <PlayIcon className="w-10 h-10 text-white" />
             </div>
        </div>
    );
};

const FinalEvaluationCard: React.FC<{ onStart: () => void; certificateAvailable: boolean }> = ({ onStart, certificateAvailable }) => {
    if (certificateAvailable) {
        return (
            <div onClick={onStart} className="flex-shrink-0 w-64 md:w-72 aspect-video rounded-lg overflow-hidden relative shadow-lg group transition-all duration-300 transform-gpu cursor-pointer hover:scale-105 hover:shadow-2xl hover:z-10 bg-yellow-600 border-2 border-yellow-400 flex flex-col items-center justify-center text-center p-4">
                <CheckCircleIcon className="w-16 h-16 text-white mb-2" />
                <h3 className="text-xl font-bold text-white">¡Curso Completado!</h3>
                <p className="text-sm text-yellow-100 mt-2">¡Felicidades! Puedes ver tu resultado y descargar tu certificado.</p>
                <div className="mt-4 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white">
                    <DownloadIcon className="w-10 h-10 text-white" />
                </div>
            </div>
        );
    }

    return (
        <div onClick={onStart} className="flex-shrink-0 w-64 md:w-72 aspect-video rounded-lg overflow-hidden relative shadow-lg group transition-all duration-300 transform-gpu cursor-pointer hover:scale-105 hover:shadow-2xl hover:z-10 bg-indigo-800 border-2 border-indigo-600 flex flex-col items-center justify-center text-center p-4">
            <h3 className="text-xl font-bold text-white">Evaluación Final</h3>
            <p className="text-sm text-indigo-200 mt-2">Demuestra todo lo que has aprendido en una evaluación final integral.</p>
             <div className="mt-4 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white">
                <PlayIcon className="w-10 h-10 text-white" />
             </div>
        </div>
    );
};

const SCENARIOS: { [key: string]: string } = {
    'prospeccion-inicial': 'Prospección Inicial / Guión Telefónico',
    'cita-diagnostico': 'Cita de Diagnóstico Financiero',
    'cita-cierre': 'Cita de Cierre',
    'manejo-objeciones': 'Práctica de Objeciones',
    'entrega-poliza': 'Entrega de Póliza',
};

const DIFFICULTIES: { [key: string]: string } = {
    'basico': 'Básico',
    'intermedio': 'Intermedio',
    'avanzado': 'Avanzado',
};

const PREDEFINED_PROFILES: { [key: string]: { name: string, description: string } } = {
    'joven-esceptico': { name: 'Alex (Joven Escéptico)', description: 'Joven profesional de 28 años, soltero, gana bien pero es escéptico sobre los seguros y cree que es muy joven para necesitarlos.' },
    'padre-preocupado': { name: 'Javier (Padre Preocupado)', description: 'Padre de familia de 40 años con dos hijos pequeños. Está muy preocupado por el futuro y la educación de sus hijos, pero tiene un presupuesto ajustado.' },
    'empresaria-ocupada': { name: 'Sofía (Empresaria Ocupada)', description: 'Dueña de una pequeña empresa de 50 años. Valora su tiempo por encima de todo y necesita soluciones rápidas y eficientes. Se enfoca en los beneficios fiscales.' },
    'cliente-informado': { name: 'Daniel (Cliente Informado)', description: 'Cliente de 35 años que ha investigado mucho por su cuenta. Viene con preguntas muy específicas y técnicas, y es propenso a comparar con otras ofertas que ha visto online.' },
};

type ViewState = 'dashboard' | 'roleplay_config' | 'roleplay_active' | 'full_route';

const sameId = (a: string | number, b: string | number) => String(a) === String(b);

const toEmbeddableUrl = (rawUrl?: string): string => {
  if (!rawUrl) return '';
  const url = rawUrl.trim();

  // Already embeddable/data url.
  if (url.startsWith('data:') || url.includes('/embed/') || url.includes('/preview')) {
    return url;
  }

  // YouTube: watch, short and shorts links -> embed.
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/
  );
  if (youtubeMatch?.[1]) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0&modestbranding=1`;
  }

  // Google Drive file links -> preview mode.
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch?.[1]) {
    return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  }

  // Google Slides links -> embed.
  if (url.includes('docs.google.com/presentation')) {
    return url
      .replace('/edit', '/embed')
      .replace('/view', '/embed')
      .replace('/pub', '/embed');
  }

  // Google Docs links -> preview.
  if (url.includes('docs.google.com/document')) {
    return url.replace('/edit', '/preview');
  }

  return url;
};

const isHttpLikeUrl = (value: string) => /^https?:\/\//i.test(value);

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; size?: 'lg' | '2xl' | '4xl' }> = ({ isOpen, onClose, title, children, size = '2xl' }) => {
    if (!isOpen) return null;
    const sizeClasses = { lg: 'max-w-lg', '2xl': 'max-w-2xl', '4xl': 'max-w-4xl' };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4" onClick={onClose}>
            <div className={`bg-slate-800 rounded-xl shadow-lg w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col border border-slate-700`} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-4 border-b border-slate-700">
                    <h3 className="text-xl font-semibold text-white">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
                </div>
                <div className="p-6 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};

const LibraryModal: React.FC<{ isOpen: boolean, onClose: () => void }> = ({ isOpen, onClose }) => {
    const [categories, setCategories] = useState<LibraryCategory[]>([]);
    const [resources, setResources] = useState<LibraryResource[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            const fetchLibraryData = async () => {
                setLoading(true);
                try {
                    const [cats, res] = await Promise.all([
                        api.getLibraryCategories(),
                        api.getLibraryResources()
                    ]);
                    setCategories(cats);
                    setResources(res);
                } catch (error) {
                    console.error("Failed to fetch library data:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchLibraryData();
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Biblioteca de Recursos" size="4xl">
            {loading ? (
                <div className="flex justify-center items-center h-64"><Spinner className="h-10 w-10 text-white" /></div>
            ) : (
                <div className="space-y-6">
                    {categories.length === 0 ? (
                         <p className="text-center text-gray-400 py-10">La biblioteca de recursos está vacía por el momento.</p>
                    ) : (
                        categories.map(category => {
                            const categoryResources = resources.filter(r => r.categoryId === category.id);
                            if (categoryResources.length === 0) return null;
                            
                            return (
                                <div key={category.id}>
                                    <h3 className="text-xl font-bold text-blue-400 border-b-2 border-slate-600 pb-2 mb-4">{category.name}</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {categoryResources.map(resource => (
                                            <div key={resource.id} className="bg-slate-700 p-4 rounded-lg flex flex-col justify-between">
                                                <div>
                                                    <h4 className="font-semibold text-white">{resource.title}</h4>
                                                    <p className="text-sm text-gray-300 mt-1">{resource.description}</p>
                                                </div>
                                                <a
                                                    href={resource.fileData}
                                                    download={resource.fileName}
                                                    className="mt-4 inline-flex items-center justify-center space-x-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 text-sm w-full"
                                                >
                                                    <DownloadIcon className="h-4 w-4" />
                                                    <span>Descargar</span>
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </Modal>
    );
};


const AdvisorDashboard: React.FC<AdvisorDashboardProps> = ({ user, onLogout, onStartEvaluation, onStartFinalEvaluation, logoUrl, onRefreshUser }) => {
  const [levels, setLevels] = useState<CurriculumLevel[]>([]);
  const [modules, setModules] = useState<CurriculumModule[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [questions, setQuestions] = useState<EvaluationQuestion[]>([]);
  const [sessions, setSessions] = useState<RolePlaySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTopic, setActiveTopic] = useState<Topic | null>(null);
  const [view, setView] = useState<ViewState>('dashboard');
  const [selectedSession, setSelectedSession] = useState<RolePlaySession | null>(null);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [modulePageById, setModulePageById] = useState<Record<string, number>>({});
  
  const [rolePlayConfig, setRolePlayConfig] = useState({
      scenarioKey: 'prospeccion-inicial',
      difficulty: 'basico',
      profileType: 'predeterminado',
      profileKey: 'joven-esceptico',
      customProfile: ''
  });
  const [isGeneratingScenario, setIsGeneratingScenario] = useState(false);
  const [generatedScenario, setGeneratedScenario] = useState<{ title: string; prompt: string } | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [curriculumData, sessionsData] = await Promise.all([
          api.getFullCurriculum(),
          api.getRolePlaySessions(user.id),
      ]);
      
      if (curriculumData) {
        // FIX: Property 'order' does not exist on type 'CurriculumLevel'. Use 'displayOrder' instead.
        setLevels(curriculumData.levels?.sort((a,b) => a.displayOrder - b.displayOrder) || []);
        // FIX: Property 'order' does not exist on type 'CurriculumModule'. Use 'displayOrder' instead.
        setModules(curriculumData.modules?.sort((a,b) => a.displayOrder - b.displayOrder) || []);
        setTopics(curriculumData.topics || []);
      }
      setSessions(sessionsData.sort((a, b) => b.date - a.date) || []);
      setQuestions([]);
      api.getAllQuestions()
        .then((allQuestions) => setQuestions(allQuestions || []))
        .catch((error) => console.error('Failed to fetch questions:', error));
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTopicSelect = async (topic: Topic) => {
    setActiveTopic(topic);
    if (user.progress[topic.id] !== TopicStatus.COMPLETED) {
      await api.updateTopicStatus(user.id, topic.id, TopicStatus.IN_PROGRESS);
      onRefreshUser();
    }
  };

  const handleMarkAsComplete = async () => {
    if (!activeTopic) return;
    setIsCompleting(true);
    try {
      await api.updateTopicStatus(user.id, activeTopic.id, TopicStatus.COMPLETED);
      onRefreshUser();
      setActiveTopic(null);
    } catch (error) {
      console.error("Failed to mark topic as complete:", error);
    } finally {
      setIsCompleting(false);
    }
  };
  
  const handleGenerateScenario = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsGeneratingScenario(true);
      
      const scenarioDescription = SCENARIOS[rolePlayConfig.scenarioKey];
      const difficultyDescription = DIFFICULTIES[rolePlayConfig.difficulty];
      const profileDescription = rolePlayConfig.profileType === 'predeterminado'
          ? PREDEFINED_PROFILES[rolePlayConfig.profileKey].description
          : rolePlayConfig.customProfile;

      const finalScenario = `Escenario: ${scenarioDescription}.\nNivel de dificultad: ${difficultyDescription}.\nPerfil del cliente: ${profileDescription}.`;
      
      const finalTitle = `${scenarioDescription} - ${rolePlayConfig.profileType === 'predeterminado' ? PREDEFINED_PROFILES[rolePlayConfig.profileKey].name : 'Personalizado'}`;

      setGeneratedScenario({ title: finalTitle, prompt: finalScenario });
      setView('roleplay_active');
      setIsGeneratingScenario(false);
  };

  const assignedTopicIdSet = useMemo(() => new Set((user.assignedTopics || []).map(String)), [user.assignedTopics]);

  const topicStatuses = useMemo(() => {
    const statuses: { [key: string]: TopicStatus } = {};

    const userAssignedModuleIds = new Set<string>();
    topics.forEach(t => {
        if (assignedTopicIdSet.has(String(t.id))) {
            userAssignedModuleIds.add(String(t.moduleId));
        }
    });
    const allSortedUserModules = levels
        // FIX: Property 'order' does not exist on type 'CurriculumLevel'. Use 'displayOrder' instead.
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .flatMap(l => 
            modules
                .filter(m => String(m.levelId) === String(l.id) && userAssignedModuleIds.has(String(m.id)))
                // FIX: Property 'order' does not exist on type 'CurriculumModule'. Use 'displayOrder' instead.
                .sort((a, b) => a.displayOrder - b.displayOrder)
        );

    let isPreviousModuleCompleted = true;

    for (const module of allSortedUserModules) {
        const topicsInModule = topics.filter(t => String(t.moduleId) === String(module.id));

        if (!isPreviousModuleCompleted) {
            topicsInModule.forEach(t => { statuses[t.id] = TopicStatus.LOCKED; });
        } else {
            topicsInModule.forEach(topic => {
                const hasPendingTopicDeps = topic.dependencies.some(
                    depId => assignedTopicIdSet.has(String(depId)) && user.progress[depId] !== TopicStatus.COMPLETED
                );
                
                if (hasPendingTopicDeps) {
                    statuses[topic.id] = TopicStatus.LOCKED;
                } else {
                    statuses[topic.id] = (user.progress[topic.id] as TopicStatus) || TopicStatus.AVAILABLE;
                }
            });
        }

        const assignedTopicsInModule = topicsInModule.filter(t => assignedTopicIdSet.has(String(t.id)));
        if (assignedTopicsInModule.length > 0) {
            const allTopicsDone = assignedTopicsInModule.every(t => user.progress[t.id] === TopicStatus.COMPLETED || user.progress[String(t.id)] === TopicStatus.COMPLETED);
            
            let evalPassed = true;
            const questionsForModule = questions.filter(q => sameId(q.moduleId, module.id));
            if (questionsForModule.length > 0) {
                const evalResult = user.progress[`eval-${module.id}`] as EvaluationResult;
                evalPassed = !!evalResult && evalResult.passed;
            }
            isPreviousModuleCompleted = allTopicsDone && evalPassed;
        } else {
            isPreviousModuleCompleted = true;
        }
    }
    
    const finalStatuses: { [key: string]: TopicStatus } = {};
    topics.forEach(topic => {
        finalStatuses[topic.id] = statuses[topic.id] || TopicStatus.LOCKED;
    });

    return finalStatuses;

  }, [levels, modules, topics, questions, assignedTopicIdSet, user.progress]);

  const getTopicStatus = useCallback((topic: Topic): TopicStatus => {
      return topicStatuses[topic.id] || TopicStatus.LOCKED;
  }, [topicStatuses]);

  const assignedTopics = useMemo(() => {
    return topics.filter(t => assignedTopicIdSet.has(String(t.id)));
  }, [topics, assignedTopicIdSet]);
  
  const levelsWithContent = useMemo(() => {
    return levels.map(level => ({
      ...level,
      modules: modules
        .filter(m => sameId(m.levelId, level.id))
        .map(module => ({
          ...module,
          topics: assignedTopics.filter(t => String(t.moduleId) === String(module.id))
        }))
        .filter(m => m.topics.length > 0)
    })).filter(l => l.modules.length > 0);
  }, [levels, modules, assignedTopics]);

  const allPrerequisitesCompleted = useMemo(() => {
    if (!assignedTopicIdSet || assignedTopicIdSet.size === 0) return false;

    // 1. Check if all assigned topics are completed.
    const allTopicsDone = Array.from(assignedTopicIdSet).every(topicId => user.progress[topicId] === TopicStatus.COMPLETED || user.progress[Number(topicId)] === TopicStatus.COMPLETED);
    if (!allTopicsDone) return false;

    // 2. Find all modules that have assigned topics.
    const modulesWithAssignedTopics = new Set<string>();
    topics.forEach(topic => {
        if (assignedTopicIdSet.has(String(topic.id))) {
            modulesWithAssignedTopics.add(topic.moduleId);
        }
    });

    // 3. For each of those modules, if it has an evaluation, check if it was passed.
    const allModuleEvalsPassed = Array.from(modulesWithAssignedTopics).every(moduleId => {
        // Check if this module has questions associated with it.
        const hasQuestions = questions.some(q => sameId(q.moduleId, moduleId));
        if (!hasQuestions) {
            return true; // No evaluation for this module, so it's "passed" by default.
        }

        // It has questions, so check for a passed result.
        const evalResult = user.progress[`eval-${moduleId}`] as EvaluationResult;
        return evalResult && evalResult.passed;
    });
    
    return allModuleEvalsPassed;

  }, [assignedTopicIdSet, user.progress, topics, questions]);

  const finalEvalResult = user.progress['eval-final-bootcamp'] as EvaluationResult;
  const certificateAvailable = !!finalEvalResult && finalEvalResult.passed;

  const handleDownloadPdf = () => {
        const doc = new jsPDF();
        doc.setFontSize(20);
        doc.text("Mi Progreso en APEX University", 14, 22);
        doc.setFontSize(12);
        doc.text(`Asesor: ${user.name}`, 14, 32);
        doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 14, 44);
        
        const tableData = levelsWithContent.flatMap(level => 
            level.modules.flatMap(module => 
                module.topics.map(topic => [
                    level.title,
                    module.title,
                    topic.title,
                    (getTopicStatus(topic) || 'Pendiente').replace(/_/g, ' ')
                ])
            )
        );

        autoTable(doc, {
            startY: 50,
            head: [['Nivel', 'Módulo', 'Tema', 'Estado']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [41, 128, 185] },
        });

        doc.save(`progreso_${user.name.replace(/\s/g, '_')}.pdf`);
  };

  const TOPICS_PER_PAGE = 4;
  const getModulePage = (moduleId: string) => modulePageById[String(moduleId)] || 0;
  const setModulePage = (moduleId: string, page: number, totalTopics: number) => {
    const maxPage = Math.max(0, Math.ceil(totalTopics / TOPICS_PER_PAGE) - 1);
    const safePage = Math.max(0, Math.min(page, maxPage));
    setModulePageById(prev => ({ ...prev, [String(moduleId)]: safePage }));
  };

  const renderActiveTopicContent = () => {
    if (!activeTopic || !activeTopic.contentUrl) {
      return <div className="text-center p-8 bg-slate-900 rounded-lg text-white h-full flex items-center justify-center">El contenido no está disponible.</div>;
    }
  
    const { contentType, contentUrl } = activeTopic;
    if (contentUrl.startsWith('lesson-builder:')) return <LessonTopicViewer lessonId={contentUrl.replace('lesson-builder:', '')} />;
    const embeddableUrl = toEmbeddableUrl(contentUrl);
  
    if (contentType === 'video' && contentUrl.startsWith('data:video/')) {
      return <video controls src={contentUrl} className="w-full h-full rounded-lg shadow-2xl mx-auto object-contain bg-black" />;
    }
  
    const isPdfDataUrl = contentUrl.startsWith('data:application/pdf');
    const isPdfLink = /\.pdf(\?|#|$)/i.test(contentUrl);
    const isPresentationLink = /(docs\.google\.com\/presentation|drive\.google\.com|\.pptx?(\?|#|$)|\.odp(\?|#|$))/i.test(contentUrl);

    // Base64 PDF renders more reliably with <embed> than iframe in several browsers.
    if (isPdfDataUrl) {
      return (
        <embed
          src={contentUrl}
          type="application/pdf"
          className="w-full h-full rounded-lg shadow-2xl mx-auto bg-white"
        />
      );
    }

    // Office docs (ppt/pptx/odp) from public URLs can be visualized with Office Web Viewer.
    if (contentType === 'presentation' && isHttpLikeUrl(contentUrl) && /\.pptx?(\?|#|$)|\.odp(\?|#|$)/i.test(contentUrl)) {
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(contentUrl)}`;
      return (
        <iframe
          src={officeViewerUrl}
          className="w-full h-full rounded-lg shadow-2xl border-none mx-auto bg-white"
          title={activeTopic.title}
          allowFullScreen
        />
      );
    }

    // Use iframe for PDF links, presentations, YouTube and embeddable content.
    return (
      <div className="w-full h-full flex flex-col">
        <iframe
          src={embeddableUrl}
          className="w-full h-full rounded-lg shadow-2xl border-none mx-auto bg-white"
          title={activeTopic.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
        {(isPdfLink || isPresentationLink) && (
          <div className="mt-2 text-xs text-slate-300 flex justify-end">
            <a href={contentUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              Si no se visualiza, abrir en nueva pestaña
            </a>
          </div>
        )}
      </div>
    );
  };
  
  const assignedCount = assignedTopics.length;
  const completedCount = assignedTopics.filter(t => getTopicStatus(t) === TopicStatus.COMPLETED).length;
  const newAssignedCount = useMemo(() => {
    if (Array.isArray(user.newAssignedTopicIds) && user.newAssignedTopicIds.length > 0) {
      return user.newAssignedTopicIds.length;
    }
    return assignedTopics.filter(topic => {
      const status = user.progress[topic.id] || user.progress[String(topic.id)];
      return status === undefined;
    }).length;
  }, [user.newAssignedTopicIds, user.progress, assignedTopics]);
  const overallProgressPct = assignedCount > 0 ? Math.round((completedCount / assignedCount) * 100) : 0;
  const currentStreak = Math.min(14, Math.max(3, Math.floor(completedCount / 2) + 3));

  if (view === 'roleplay_active' && generatedScenario) {
     return <div className="h-screen bg-slate-900 p-4"><RolePlayView user={user} scenario={generatedScenario.prompt} scenarioTitle={generatedScenario.title} onFinish={() => { setView('dashboard'); setGeneratedScenario(null); fetchData(); }}/></div>;
  }

  return (
    <div className="min-h-screen bg-[#020617] text-white">
      <Header user={user} onLogout={onLogout} logoUrl={logoUrl} />
      {activeTopic ? (
        <div className="fixed inset-0 bg-black/80 z-40 flex justify-center items-center p-4 animate-fade-in">
            <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-6xl h-[95vh] flex flex-col border border-slate-700" onClick={e => e.stopPropagation()}>
                <header className="flex-shrink-0 p-4 border-b border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">{activeTopic.title}</h2>
                    <button onClick={() => setActiveTopic(null)} className="text-gray-400 hover:text-white text-3xl">&times;</button>
                </header>
                <main className="flex-1 overflow-hidden p-2 sm:p-4 bg-slate-900">
                    {renderActiveTopicContent()}
                </main>
                <footer className="flex-shrink-0 p-4 border-t border-slate-700 flex justify-end items-center space-x-4">
                    <button onClick={() => setActiveTopic(null)} className="bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-slate-500">
                        Cerrar
                    </button>
                    {getTopicStatus(activeTopic) !== TopicStatus.COMPLETED && (
                        <button 
                            onClick={handleMarkAsComplete}
                            disabled={isCompleting}
                            className="bg-green-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                        >
                            {isCompleting ? <Spinner className="h-5 w-5 mr-2" /> : <CheckCircleIcon className="h-5 w-5 mr-2" />}
                            Marcar como Completado
                        </button>
                    )}
                </footer>
            </div>
        </div>
      ) : (
        <>
          <div className="relative overflow-hidden">
            <div className="pointer-events-none absolute -top-40 -left-20 h-96 w-96 rounded-full bg-fuchsia-600/20 blur-[120px]" />
            <div className="pointer-events-none absolute top-20 right-0 h-96 w-96 rounded-full bg-cyan-500/20 blur-[120px]" />
            <div className="mx-auto max-w-[1700px] px-3 py-4 md:px-6 md:py-6">
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[260px_1fr]">
                <aside className="hidden xl:block">
                  <div className="sticky top-6 rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur-xl">
                    {[
                      ['Inicio', BookOpenIcon, true],
                      ['Mi Ruta', BookOpenIcon, false],
                      ['Bootcamps', CheckCircleIcon, false],
                      ['Biblioteca', FolderIcon, false],
                      ['Arena de Práctica', RobotIcon, false],
                      ['Historial', UserGroupIcon, false],
                      ['Mi Progreso', DownloadIcon, false],
                      ['Comunidad', UserGroupIcon, false],
                      ['Calendario', PencilIcon, false],
                      ['Mensajes', PlayIcon, false],
                      ['Ajustes', ArrowLeftIcon, false],
                    ].map(([label, Icon, active]) => (
                      <button key={String(label)} className={`mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-all ${active ? 'bg-gradient-to-r from-indigo-600/80 to-violet-600/80 font-semibold text-white shadow-lg shadow-indigo-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}>
                        <Icon className="h-4 w-4" />
                        <span>{label}</span>
                      </button>
                    ))}
                    <div className="mt-5 rounded-xl border border-violet-400/20 bg-gradient-to-br from-violet-600/20 to-cyan-500/10 p-4">
                      <p className="text-xs uppercase tracking-wider text-violet-200">Racha de aprendizaje</p>
                      <p className="mt-2 text-3xl font-bold">{currentStreak}</p>
                      <p className="text-sm text-slate-300">días seguidos</p>
                    </div>
                  </div>
                </aside>

                <main className="space-y-6 min-w-0">
                  <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-900/70 via-slate-900/90 to-fuchsia-900/40 p-6 md:p-8">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.06]" />
                    <div className="relative z-10 grid gap-6 lg:grid-cols-[1.5fr_1fr]">
                      <div>
                        <p className="text-sm text-indigo-200">Bienvenido de vuelta, {user.name.split(' ')[0]} 👋</p>
                        <h1 className="mt-2 text-3xl font-bold leading-tight md:text-5xl">Tu centro de mando para convertirte en un <span className="bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">asesor extraordinario</span>.</h1>
                        <p className="mt-4 max-w-2xl text-slate-300">Avanza por tu ruta, supera evaluaciones y fortalece tu práctica con IA. Todo en un solo lugar.</p>
                        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                          <div className="rounded-xl border border-white/10 bg-black/20 p-4"><p className="text-xs text-slate-400">Progreso General</p><p className="mt-1 text-2xl font-bold">{overallProgressPct}%</p></div>
                          <div className="rounded-xl border border-white/10 bg-black/20 p-4"><p className="text-xs text-slate-400">Nivel Actual</p><p className="mt-1 text-2xl font-bold">{overallProgressPct > 70 ? 'Avanzado' : overallProgressPct > 35 ? 'Intermedio' : 'Inicial'}</p></div>
                          <div className="rounded-xl border border-white/10 bg-black/20 p-4"><p className="text-xs text-slate-400">Próximo Hito</p><p className="mt-1 text-2xl font-bold">{Math.min(100, overallProgressPct + 15)}%</p></div>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 backdrop-blur-xl">
                        <h3 className="text-base font-semibold">Continuar Aprendiendo</h3>
                        <p className="mt-2 text-sm text-slate-300">Tienes {assignedCount - completedCount} temas pendientes para desbloquear tu siguiente evaluación.</p>
                        <div className="mt-4 h-2 rounded-full bg-slate-700"><div className="h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all" style={{ width: `${overallProgressPct}%` }} /></div>
                        <button className="mt-5 w-full rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-2.5 font-semibold transition hover:brightness-110" onClick={() => window.scrollTo({ top: 520, behavior: 'smooth' })}>Ver Mi Ruta</button>
                        <button className="mt-3 w-full rounded-xl border border-indigo-400/40 px-4 py-2.5 text-sm font-semibold text-indigo-100 transition hover:bg-indigo-500/20" onClick={() => setView('full_route')}>Ver Ruta Completa</button>
                      </div>
                    </div>
                  </section>

                  <main className="space-y-6 min-w-0">
            {loading ? (
              <div className="grid gap-4 md:grid-cols-3">
                {[...Array(6)].map((_, idx) => <div key={idx} className="h-28 animate-pulse rounded-2xl border border-white/10 bg-white/5" />)}
              </div>
            ) : (
              <div className="space-y-8">
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 md:p-6 shadow-2xl border border-white/10">
                  <div className="flex flex-wrap justify-between gap-3 items-center mb-4">
                    <h2 className="text-xl md:text-2xl font-bold text-white flex items-center"><BookOpenIcon className="h-6 w-6 mr-3 text-blue-400"/>Mi Ruta de Aprendizaje</h2>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setView('full_route')} className="flex items-center space-x-2 bg-slate-700/70 text-white font-semibold py-2 px-4 rounded-xl hover:bg-slate-600 text-sm transition">
                        <BookOpenIcon className="h-4 w-4" />
                        <span>Ver Ruta Completa</span>
                      </button>
                      <button onClick={handleDownloadPdf} className="flex items-center space-x-2 bg-indigo-600/60 text-white font-semibold py-2 px-4 rounded-xl hover:bg-indigo-600 text-sm transition">
                        <DownloadIcon className="h-4 w-4" />
                        <span>Descargar Progreso</span>
                      </button>
                    </div>
                  </div>
                  {newAssignedCount > 0 && (
                    <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-amber-100">
                      <p className="font-semibold">Tienes {newAssignedCount} tema(s) nuevo(s) asignado(s) a tu currícula.</p>
                      <p className="text-sm text-amber-200/90">Tu ruta se actualizó automáticamente. Revisa los módulos para comenzar con el nuevo contenido.</p>
                    </div>
                  )}
                </div>

                {levelsWithContent.map(level => (
                  <div key={level.id} className="animate-fade-in">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 pl-4 border-l-4 border-blue-500">{level.title}</h2>
                    <p className="text-gray-400 mb-6 ml-4">{level.description}</p>
                    {level.modules.map(module => {
                      const moduleTopics = module.topics;
                      const assignedTopicsInModule = moduleTopics.filter(t => assignedTopicIdSet.has(String(t.id)));
                      const completedTopicsCount = assignedTopicsInModule.filter(t => user.progress[t.id] === TopicStatus.COMPLETED || user.progress[String(t.id)] === TopicStatus.COMPLETED).length;
                      const isModuleComplete = assignedTopicsInModule.length > 0 && completedTopicsCount === assignedTopicsInModule.length;
                      const evalResult = user.progress[`eval-${module.id}`] as EvaluationResult;
                      const questionsForModuleExist = questions.some(q => sameId(q.moduleId, module.id));

                      return (
                        <div key={module.id} className="mb-8 rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-800/60 p-4">
                          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <h3 className="text-xl font-semibold text-gray-200 flex items-center">{module.title} {isModuleComplete && <CheckCircleIcon className="h-6 w-6 ml-2 text-green-500"/>}</h3>
                            <span className="text-xs text-slate-300">{completedTopicsCount}/{assignedTopicsInModule.length} completados</span>
                          </div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            {moduleTopics.slice(getModulePage(String(module.id)) * TOPICS_PER_PAGE, (getModulePage(String(module.id)) + 1) * TOPICS_PER_PAGE).map(topic => (
                              <TopicCard
                                key={topic.id}
                                topic={topic}
                                status={getTopicStatus(topic)}
                                onSelect={() => handleTopicSelect(topic)}
                              />
                            ))}
                          </div>
                          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setModulePage(String(module.id), getModulePage(String(module.id)) - 1, moduleTopics.length)}
                                disabled={getModulePage(String(module.id)) === 0}
                                className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm disabled:opacity-40"
                              >
                                Anterior 4
                              </button>
                              <button
                                onClick={() => setModulePage(String(module.id), getModulePage(String(module.id)) + 1, moduleTopics.length)}
                                disabled={(getModulePage(String(module.id)) + 1) * TOPICS_PER_PAGE >= moduleTopics.length}
                                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
                              >
                                Siguientes 4
                              </button>
                            </div>
                            <p className="text-xs text-slate-400">Página {Math.min(getModulePage(String(module.id)) + 1, Math.max(1, Math.ceil(moduleTopics.length / TOPICS_PER_PAGE)))} de {Math.max(1, Math.ceil(moduleTopics.length / TOPICS_PER_PAGE))}</p>
                          </div>
                          {isModuleComplete && questionsForModuleExist && (
                            <div className="mt-4 flex">
                               <EvaluationCard module={module} onStartEvaluation={onStartEvaluation} evalResult={evalResult} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                
                {allPrerequisitesCompleted && (
                    <div className="animate-fade-in">
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 pl-4 border-l-4 border-yellow-400">Cierre del Curso</h2>
                         <div className="flex overflow-x-auto space-x-4 md:space-x-6 pb-4 carousel-scrollbar">
                            <FinalEvaluationCard 
                                onStart={onStartFinalEvaluation}
                                certificateAvailable={certificateAvailable}
                            />
                        </div>
                    </div>
                )}

                 <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10 animate-fade-in transition hover:border-cyan-400/40">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 className="text-xl md:text-2xl font-bold text-white flex items-center"><FolderIcon className="h-6 w-6 mr-3 text-cyan-400"/>Biblioteca de Recursos</h2>
                            <p className="text-gray-400 mt-2">Accede a material de apoyo, guiones y documentos útiles para tu formación.</p>
                        </div>
                        <button 
                            onClick={() => setIsLibraryOpen(true)}
                            className="mt-4 md:mt-0 flex-shrink-0 bg-cyan-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-cyan-500 transition-colors flex items-center justify-center space-x-2"
                        >
                            <BookOpenIcon className="h-5 w-5" />
                            <span>Abrir Biblioteca</span>
                        </button>
                    </div>
                </div>
                
                 <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10 animate-fade-in transition hover:border-fuchsia-400/40">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 className="text-xl md:text-2xl font-bold text-white flex items-center"><RobotIcon className="h-6 w-6 mr-3 text-purple-400"/>Arena de Práctica</h2>
                            <p className="text-gray-400 mt-2">Perfecciona tus habilidades de comunicación y manejo de objeciones con simulaciones de IA realistas.</p>
                        </div>
                        <button 
                            onClick={() => setView('roleplay_config')}
                            className="mt-4 md:mt-0 flex-shrink-0 bg-purple-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-purple-500 transition-colors flex items-center justify-center space-x-2"
                        >
                            <PlayIcon className="h-5 w-5" />
                            <span>Iniciar Práctica</span>
                        </button>
                    </div>
                </div>


                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 md:p-6 shadow-2xl border border-white/10 animate-fade-in">
                  <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl md:text-2xl font-bold text-white flex items-center"><UserGroupIcon className="h-6 w-6 mr-3 text-green-400"/>Historial de Prácticas</h2>
                  </div>
                  {sessions.length > 0 ? (
                      <div className="overflow-x-auto">
                          <table className="min-w-full text-left">
                              <thead className="border-b border-slate-600">
                                  <tr>
                                      <th className="py-2 px-3 text-sm font-semibold text-gray-400">Fecha</th>
                                      <th className="py-2 px-3 text-sm font-semibold text-gray-400">Escenario</th>
                                      <th className="py-2 px-3 text-sm font-semibold text-gray-400">Puntuación</th>
                                      <th className="py-2 px-3 text-sm font-semibold text-gray-400"></th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {sessions.map(session => (
                                      <tr key={session.id} className="border-b border-slate-700 last:border-b-0 hover:bg-slate-700/50">
                                          <td className="py-3 px-3 text-sm text-gray-300">{new Date(session.date).toLocaleString('es-ES')}</td>
                                          <td className="py-3 px-3 text-sm text-white font-medium">{session.scenarioTitle}</td>
                                          <td className="py-3 px-3 text-sm font-bold text-blue-400">{session.feedback.overallScore}%</td>
                                          <td className="py-3 px-3 text-right">
                                              <button onClick={() => setSelectedSession(session)} className="text-sm font-semibold text-blue-400 hover:text-blue-300">Ver Detalles</button>
                                          </td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      </div>
                  ) : (
                      <p className="text-center text-gray-500 py-8">Aún no has completado ninguna práctica de role play.</p>
                  )}
                </div>
              </div>
            )}
                  </main>
                </main>
              </div>
            </div>
          </div>
        </>
      )}
      {!activeTopic && view === 'full_route' && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-[#020617] p-4 md:p-8">
          <div className="mx-auto w-full max-w-7xl rounded-2xl border border-white/10 bg-slate-900/90 p-4 md:p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-2xl font-bold">Ruta Completa</h2>
              <button onClick={() => setView('dashboard')} className="rounded-lg bg-indigo-600 px-4 py-2 font-semibold">Volver al Dashboard</button>
            </div>
            <div className="space-y-6">
              {levelsWithContent.map(level => (
                <div key={`full-${level.id}`} className="rounded-xl border border-white/10 bg-slate-800/50 p-4">
                  <h3 className="text-xl font-bold text-cyan-300">{level.title}</h3>
                  <p className="mb-4 text-sm text-slate-300">{level.description}</p>
                  <div className="space-y-4">
                    {level.modules.map(module => (
                      <div key={`full-module-${module.id}`} className="rounded-lg border border-slate-700 p-3">
                        <h4 className="mb-3 font-semibold">{module.title}</h4>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                          {module.topics.map(topic => (
                            <TopicCard key={`full-topic-${topic.id}`} topic={topic} status={getTopicStatus(topic)} onSelect={() => handleTopicSelect(topic)} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
       <LibraryModal isOpen={isLibraryOpen} onClose={() => setIsLibraryOpen(false)} />

       <Modal isOpen={view === 'roleplay_config'} onClose={() => setView('dashboard')} title="Configurar Práctica de Role Play">
         <form onSubmit={handleGenerateScenario} className="space-y-6 text-gray-200">
              <div>
                  <label htmlFor="scenario" className="block text-sm font-medium mb-1">Selecciona un Escenario</label>
                  <select
                      id="scenario"
                      value={rolePlayConfig.scenarioKey}
                      onChange={(e) => setRolePlayConfig(p => ({ ...p, scenarioKey: e.currentTarget.value }))}
                      className="w-full bg-slate-700 border-slate-600 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                      {Object.entries(SCENARIOS).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                      ))}
                  </select>
              </div>
              
              <div>
                  <label htmlFor="difficulty" className="block text-sm font-medium mb-1">Nivel de Dificultad</label>
                  <select
                      id="difficulty"
                      value={rolePlayConfig.difficulty}
                      onChange={(e) => setRolePlayConfig(p => ({ ...p, difficulty: e.currentTarget.value }))}
                      className="w-full bg-slate-700 border-slate-600 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                      {Object.entries(DIFFICULTIES).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                      ))}
                  </select>
              </div>

              <div>
                  <label className="block text-sm font-medium mb-2">Perfil del Cliente</label>
                  <div className="flex space-x-4">
                      <label className="flex items-center">
                          <input type="radio" name="profileType" value="predeterminado" checked={rolePlayConfig.profileType === 'predeterminado'} onChange={(e) => setRolePlayConfig(p => ({...p, profileType: e.currentTarget.value}))} className="text-blue-500 focus:ring-blue-500"/>
                          <span className="ml-2">Predeterminado</span>
                      </label>
                      <label className="flex items-center">
                          <input type="radio" name="profileType" value="personalizado" checked={rolePlayConfig.profileType === 'personalizado'} onChange={(e) => setRolePlayConfig(p => ({...p, profileType: e.currentTarget.value}))} className="text-blue-500 focus:ring-blue-500"/>
                          <span className="ml-2">Personalizado</span>
                      </label>
                  </div>
              </div>
              
              {rolePlayConfig.profileType === 'predeterminado' ? (
                  <div className="animate-fade-in">
                      <label htmlFor="profileKey" className="block text-sm font-medium mb-1">Selecciona un Perfil</label>
                      <select
                          id="profileKey"
                          value={rolePlayConfig.profileKey}
                          onChange={(e) => setRolePlayConfig(p => ({ ...p, profileKey: e.currentTarget.value }))}
                          className="w-full bg-slate-700 border-slate-600 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                          {Object.entries(PREDEFINED_PROFILES).map(([key, {name}]) => (
                              <option key={key} value={key}>{name}</option>
                          ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-2">{PREDEFINED_PROFILES[rolePlayConfig.profileKey].description}</p>
                  </div>
              ) : (
                  <div className="animate-fade-in">
                      <label htmlFor="customProfile" className="block text-sm font-medium mb-1">Describe el Perfil del Cliente</label>
                      <textarea
                          id="customProfile"
                          rows={4}
                          value={rolePlayConfig.customProfile}
                          onChange={(e) => setRolePlayConfig(p => ({ ...p, customProfile: e.currentTarget.value }))}
                          className="w-full bg-slate-700 border-slate-600 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ej: Hombre de 45 años, ingeniero, casado con 2 hijos. Le preocupa la universidad de sus hijos pero desconfía de los asesores..."
                          required
                      ></textarea>
                  </div>
              )}

              <div className="pt-4 flex justify-end">
                  <button
                      type="submit"
                      disabled={isGeneratingScenario}
                      className="flex items-center justify-center bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-wait"
                  >
                      {isGeneratingScenario ? <Spinner className="h-5 w-5 mr-2" /> : <RobotIcon className="h-5 w-5 mr-2" />}
                      {isGeneratingScenario ? 'Iniciando...' : 'Iniciar Práctica'}
                  </button>
              </div>
          </form>
       </Modal>
       
       <Modal isOpen={!!selectedSession} onClose={() => setSelectedSession(null)} title="Detalle de Sesión de Role Play" size="4xl">
           {selectedSession && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-white">
                    <div className="bg-slate-700 p-4 rounded-lg">
                        <h4 className="font-bold mb-2 text-gray-200">Transcripción</h4>
                        <div className="h-96 overflow-y-auto space-y-3 p-2 border rounded bg-slate-800 border-slate-600">
                            {selectedSession.transcript.map((msg, index) => (
                                <div key={index} className={`flex items-start gap-2 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-xs px-3 py-2 rounded-lg ${msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-600 text-gray-200'}`}>
                                        <p className="text-sm">{msg.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg">
                        <h4 className="font-bold mb-2 text-gray-200">Feedback del Coach de IA</h4>
                        <div className="space-y-4">
                            <div className="text-center p-4 bg-slate-800 rounded-lg border border-slate-600">
                                <p className="text-sm text-gray-400">Puntuación</p>
                                <p className="text-4xl font-bold text-blue-400">{selectedSession.feedback.overallScore}%</p>
                            </div>
                            <div>
                                <h5 className="font-semibold text-green-400">Puntos Fuertes</h5>
                                <ul className="list-disc list-inside text-sm text-gray-300 mt-1 pl-2">{selectedSession.feedback.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                            </div>
                            <div>
                                <h5 className="font-semibold text-yellow-400">Áreas de Mejora</h5>
                                <ul className="list-disc list-inside text-sm text-gray-300 mt-1 pl-2">{selectedSession.feedback.areasForImprovement.map((a, i) => <li key={i}>{a}</li>)}</ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
       </Modal>
    </div>
  );
};

export default AdvisorDashboard;
